import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import type { CreateNewStoreRequest } from "@/services/authService";
import { StandardFormInput, StandardFormSelect } from "@/components/common/StandardFormInput";
import { StandardCheckbox } from "@/components/common/StandardCheckbox";
import { ButtonPrimary, ButtonOutline } from "@/components/common/StandardButton";
import { transformStatesCitiesData } from "@/utils/statesCitiesTransform";
import { whitelabelConfig } from "@/config/whitelabel";

const configuredStoreDashboardPath = `/${whitelabelConfig.storeType}/${whitelabelConfig.storeId}/dashboard`;
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Building2,
  CheckCircle2,
  FileText,
  ShieldCheck,
  Store,
  UserRound,
} from "lucide-react";

type OnboardingStep = 1 | 2 | 3 | 4;
type FormErrors = Partial<
  Record<
    keyof Omit<CreateNewStoreRequest, "store_type"> | "store_type" | "terms",
    string
  >
>;

interface City {
  id: number;
  name: string;
}

interface StateOption {
  id: number;
  name: string;
  cities: City[];
}

const stepConfig: Record<
  OnboardingStep,
  { title: string; description: string; icon: React.ComponentType<{ className?: string }> }
> = {
  1: {
    title: "Store Setup",
    description: "Your account will be created as a seller store.",
    icon: Store,
  },
  2: {
    title: "Personal Details",
    description: "These details will be linked with your account.",
    icon: UserRound,
  },
  3: {
    title: "Business Details",
    description: "Used to initialize store profile and prefill KYC.",
    icon: Building2,
  },
  4: {
    title: "Terms & Agreement",
    description: "Accept policies to complete store setup.",
    icon: ShieldCheck,
  },
};

export default function AuthNewStoreIndex() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(1);
  const [error, setError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [states, setStates] = useState<StateOption[]>([]);
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(true);
  const [formData, setFormData] = useState<Omit<CreateNewStoreRequest, "store_type">>({
    personal_name: "",
    personal_email: "",
    personal_phone_number: "",
    business_name: "",
    business_email: "",
    business_phone_number: "",
    address_line_one: "",
    address_line_two: "",
    city: "",
    state: "",
    postal_code: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const {
    statusLoading,
    isAuthenticated,
    isLoading,
    error: authError,
    currentPhoneNumber,
    stores,
    createStore,
    logout,
  } = useAuth("new-store");
  const hasStores = stores.length > 0;

  useEffect(() => {
    const fallbackPhone = currentPhoneNumber;
    if (!fallbackPhone) return;

    setFormData((prev) => {
      if (prev.personal_phone_number.trim()) return prev;
      return {
        ...prev,
        personal_phone_number: fallbackPhone,
      };
    });
  }, [currentPhoneNumber]);

  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  useEffect(() => {
    const loadStates = async () => {
      try {
        const response = await fetch("/states-cities.json");
        const rawData: Record<string, string[]> = await response.json();
        const transformedData = transformStatesCitiesData(rawData).map((state) => ({
          id: state.id,
          name: state.name,
          cities: state.cities.map((city) => ({ id: city.id, name: city.name })),
        }));
        setStates(transformedData);
      } catch (loadError) {
        console.error("Failed to load states-cities data:", loadError);
      } finally {
        setIsLoadingStates(false);
      }
    };

    loadStates();
  }, []);

  useEffect(() => {
    if (!formData.state) {
      setAvailableCities([]);
      return;
    }
    const selected = states.find((state) => state.name === formData.state);
    setAvailableCities(selected?.cities || []);

    if (formData.city && selected && !selected.cities.some((city) => city.name === formData.city)) {
      setFormData((prev) => ({ ...prev, city: "" }));
    }
  }, [formData.state, formData.city, states]);

  const handleInputChange = (
    field: keyof Omit<CreateNewStoreRequest, "store_type">,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateStep = (step: OnboardingStep) => {
    const nextErrors: FormErrors = {};
    if (step === 2) {
      if (!formData.personal_name.trim()) {
        nextErrors.personal_name = "Personal name is required";
      }
      if (!formData.personal_email.trim()) {
        nextErrors.personal_email = "Personal email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personal_email.trim())) {
        nextErrors.personal_email = "Enter a valid email address";
      }
      const personalPhoneDigits = formData.personal_phone_number.replace(/\D/g, "");
      if (personalPhoneDigits.length !== 10) {
        nextErrors.personal_phone_number = "Enter a valid 10-digit phone number";
      }
    }
    if (step === 3) {
      if (!formData.business_name.trim()) {
        nextErrors.business_name = "Business name is required";
      }
      if (!formData.business_email.trim()) {
        nextErrors.business_email = "Business email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.business_email.trim())) {
        nextErrors.business_email = "Enter a valid email address";
      }
      const businessPhoneDigits = formData.business_phone_number.replace(/\D/g, "");
      if (businessPhoneDigits.length !== 10) {
        nextErrors.business_phone_number = "Enter a valid 10-digit phone number";
      }
      if (!formData.address_line_one.trim()) {
        nextErrors.address_line_one = "Address line 1 is required";
      }
      if (!formData.city.trim()) nextErrors.city = "City is required";
      if (!formData.state.trim()) nextErrors.state = "State is required";
      if (!formData.postal_code.trim()) nextErrors.postal_code = "Postal code is required";
    }
    if (step === 4 && !acceptedTerms) {
      nextErrors.terms = "You must accept Terms & Privacy Policy";
    }
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const goNext = () => {
    if (!validateStep(currentStep)) return;
    setCurrentStep((prev) => Math.min(4, prev + 1) as OnboardingStep);
    setError(null);
  };

  const goBack = () => {
    if (currentStep === 1 && hasStores) {
      navigate(configuredStoreDashboardPath, { replace: true });
      return;
    }

    if (currentStep === 1) {
      handleLogout();
      return;
    }

    setCurrentStep((prev) => Math.max(1, prev - 1) as OnboardingStep);
    setError(null);
  };

  const handleLogout = () => {
    logout();
  };

  const onCreateStore = async () => {
    if (!validateStep(4)) return;
    setError(null);
    createStore({
      store_type: "seller",
      ...formData,
      personal_phone_number: formData.personal_phone_number.replace(/\D/g, ""),
      business_phone_number: formData.business_phone_number.replace(/\D/g, ""),
    });
  };

  if (statusLoading) {
    return (
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">
          Checking authentication...
        </p>
      </div>
    );
  }

  if (!statusLoading && !isAuthenticated) {
    return null;
  }

  return (
    <>
      <div className="text-center mb-8">
        <img
          src={whitelabelConfig.logos.auth}
          alt={`${whitelabelConfig.brandName} logo`}
          width={90}
          height={90}
          className="mx-auto mb-4"
        />
        <h1 className="text-3xl font-semibold text-foreground mb-2">
          Create New Store
        </h1>
        <p className="text-muted-foreground mt-2">
          Complete this 4-step onboarding to set up account, personal, and business details.
        </p>
        <div className="mt-6 flex items-center justify-between gap-4 rounded-2xl border border-border bg-card px-4 py-3 text-left shadow-theme-sm">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Logged in as
            </p>
            <p className="truncate text-sm font-medium text-foreground">
              {currentPhoneNumber || "Phone number unavailable"}
            </p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="shrink-0 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            Log out
          </button>
        </div>
        {error && (
          <div className="mt-4 p-3 rounded-lg border border-destructive/25 bg-destructive/10">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}
      </div>

      <div className="space-y-6 max-w-3xl mx-auto w-full">
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="space-y-2">
              <div
                className={`h-2 rounded-full ${
                  currentStep >= step ? "bg-primary" : "bg-muted dark:bg-muted/80"
                }`}
              />
              <div className="hidden sm:flex items-center gap-1 text-[11px] text-muted-foreground">
                {currentStep > step ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                ) : (
                  <div className="h-3.5 w-3.5 rounded-full border border-border" />
                )}
                <span>Step {step}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 space-y-5 shadow-theme-sm">
          <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/50 dark:bg-muted/30 px-4 py-3">
            {(() => {
              const Icon = stepConfig[currentStep].icon;
              return <Icon className="h-5 w-5 text-primary mt-0.5 shrink-0" />;
            })()}
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                {stepConfig[currentStep].title}
              </p>
              <p className="text-xs text-muted-foreground">
                {stepConfig[currentStep].description}
              </p>
            </div>
          </div>

          {currentStep === 1 && (
            <>
              <div>
                <h2 className="text-xl font-medium text-foreground">
                  1. Store Setup
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  This workspace is configured for seller stores.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div className="w-full p-4 border rounded-xl text-left border-primary bg-primary/10 shadow-sm ring-1 ring-primary/20 dark:bg-primary/15">
                  <div className="flex items-center gap-2">
                    <Store className="h-4 w-4 text-primary shrink-0" />
                    <h3 className="text-foreground font-medium">Seller</h3>
                  </div>
                  <p className="text-muted-foreground text-sm mt-1">
                    Sell directly to end customers.
                  </p>
                </div>
              </div>
            </>
          )}

          {currentStep === 2 && (
            <>
              <div>
                <h2 className="text-xl font-medium text-foreground">
                  2. Personal Details
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <StandardFormInput
                    label="Full Name"
                    value={formData.personal_name}
                    onChange={(e) => handleInputChange("personal_name", e.target.value)}
                    placeholder="Enter your full name"
                    error={formErrors.personal_name}
                  />
                </div>
                <div>
                  <StandardFormInput
                    label="Personal Email"
                    type="email"
                    value={formData.personal_email}
                    onChange={(e) => handleInputChange("personal_email", e.target.value)}
                    placeholder="name@example.com"
                    error={formErrors.personal_email}
                  />
                </div>
                <div>
                  <StandardFormInput
                    label="Personal Phone"
                    value={formData.personal_phone_number}
                    onChange={(e) =>
                      handleInputChange(
                        "personal_phone_number",
                        e.target.value.replace(/\D/g, "").slice(0, 10),
                      )
                    }
                    placeholder="9876543210"
                    error={formErrors.personal_phone_number}
                  />
                </div>
              </div>
            </>
          )}

          {currentStep === 3 && (
            <>
              <div>
                <h2 className="text-xl font-medium text-foreground">
                  3. Business Details
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <StandardFormInput
                    label="Business Name"
                    value={formData.business_name}
                    onChange={(e) => handleInputChange("business_name", e.target.value)}
                    placeholder="Acme Retail"
                    error={formErrors.business_name}
                  />
                </div>
                <div>
                  <StandardFormInput
                    label="Business Email"
                    type="email"
                    value={formData.business_email}
                    onChange={(e) => handleInputChange("business_email", e.target.value)}
                    placeholder="owner@business.com"
                    error={formErrors.business_email}
                  />
                </div>
                <div>
                  <StandardFormInput
                    label="Business Phone"
                    value={formData.business_phone_number}
                    onChange={(e) =>
                      handleInputChange(
                        "business_phone_number",
                        e.target.value.replace(/\D/g, "").slice(0, 10),
                      )
                    }
                    placeholder="9876543210"
                    error={formErrors.business_phone_number}
                  />
                </div>
                <div>
                  <StandardFormInput
                    label="Postal Code"
                    value={formData.postal_code}
                    onChange={(e) => handleInputChange("postal_code", e.target.value)}
                    placeholder="400001"
                    error={formErrors.postal_code}
                  />
                </div>
              </div>
              <div>
                <StandardFormInput
                  label="Address Line 1"
                  value={formData.address_line_one}
                  onChange={(e) => handleInputChange("address_line_one", e.target.value)}
                  placeholder="Street address"
                  error={formErrors.address_line_one}
                />
              </div>
              <div>
                <StandardFormInput
                  label="Address Line 2 (optional)"
                  value={formData.address_line_two || ""}
                  onChange={(e) => handleInputChange("address_line_two", e.target.value)}
                  placeholder="Apartment, suite, etc."
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <StandardFormSelect
                    label="State"
                    value={formData.state}
                    onValueChange={(value) => {
                      setFormData((prev) => ({
                        ...prev,
                        state: value,
                        city: "",
                      }));
                      if (formErrors.state || formErrors.city) {
                        setFormErrors((prev) => ({
                          ...prev,
                          state: undefined,
                          city: undefined,
                        }));
                      }
                    }}
                    options={states.map((state) => ({
                      label: state.name,
                      value: state.name,
                    }))}
                    placeholder={isLoadingStates ? "Loading states..." : "Select State"}
                    disabled={isLoadingStates}
                    error={formErrors.state}
                  />
                </div>
                <div>
                  <StandardFormSelect
                    label="City"
                    value={formData.city}
                    onValueChange={(value) => handleInputChange("city", value)}
                    options={availableCities.map((city) => ({
                      label: city.name,
                      value: city.name,
                    }))}
                    placeholder={
                      !formData.state
                        ? "Select state first"
                        : availableCities.length === 0
                          ? "No cities available"
                          : "Select City"
                    }
                    disabled={!formData.state || availableCities.length === 0}
                    error={formErrors.city}
                  />
                </div>
              </div>
            </>
          )}

          {currentStep === 4 && (
            <>
              <div>
                <h2 className="text-xl font-medium text-foreground flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary shrink-0" />
                  <span>4. Terms & Agreement</span>
                </h2>
              </div>
              <label className="flex items-start gap-3 p-4 rounded-xl border border-border bg-background dark:bg-muted/50 cursor-pointer hover:bg-muted/40 dark:hover:bg-muted/70 transition-colors">
                <StandardCheckbox
                  id="new-store-terms"
                  label=""
                  checked={acceptedTerms}
                  onCheckedChange={(checked) => {
                    setAcceptedTerms(checked);
                    if (formErrors.terms) {
                      setFormErrors((prev) => ({ ...prev, terms: undefined }));
                    }
                  }}
                  className="mt-0.5"
                />
                <span className="text-sm text-foreground">
                  I agree to the{" "}
                  <a href="#" className="text-primary underline inline-flex items-center gap-1">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a href="#" className="text-primary underline inline-flex items-center gap-1">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    Terms & Conditions
                  </a>.
                </span>
              </label>
              {formErrors.terms && (
                <p className="text-xs text-destructive">{formErrors.terms}</p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <ButtonOutline
            onClick={goBack}
            disabled={isLoading}
            className="inline-flex items-center gap-2"
          >
            {currentStep === 1 && hasStores ? (
              <>
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </>
            ) : currentStep === 1 ? (
              "Log out"
            ) : (
              <>
                <ArrowLeft className="h-4 w-4" />
                Back
              </>
            )}
          </ButtonOutline>
          {currentStep < 4 ? (
            <ButtonPrimary
              onClick={goNext}
              disabled={isLoading}
              className="inline-flex items-center gap-2"
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </ButtonPrimary>
          ) : (
            <ButtonPrimary
              onClick={onCreateStore}
              disabled={isLoading}
              className="inline-flex items-center gap-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              {isLoading ? "Creating..." : "Create Store"}
            </ButtonPrimary>
          )}
        </div>
      </div>
    </>
  );
}
